/**
 * Keeping the access token fresh, so it does not have to be long-lived.
 *
 * ## Why this exists
 *
 * Both tokens are stored in `localStorage`, readable by any script in the page
 * (QA ledger AUTH2-003). The CSP is good — `script-src 'self'`, no
 * `unsafe-inline`, no `unsafe-eval` — which closes the common injection routes,
 * but it is one layer. What made the residual risk sharp was the *window*: the
 * access token lived **24 hours**, so a single exfiltration yielded a bearer
 * credential that kept working for a day, from anywhere, with no further access
 * to the victim's browser. And `accounts` is a map, so it yielded one per
 * signed-in account.
 *
 * Shortening that window is most of the mitigation and needs no architectural
 * change — but it was not available, because **nothing ever refreshed**. The
 * server has had `POST /api/auth/refresh` all along and the client stored a
 * refresh token it never sent: a 401 simply ended the session. Cutting the TTL
 * without this would have logged everyone out every few minutes.
 *
 * So: refresh ahead of expiry, and the short TTL becomes affordable.
 *
 * ## Why proactive rather than on-401
 *
 * A retry-after-401 interceptor would have to live in every plugin's API client
 * (there are a dozen, each with its own `apiFetch`) and would have to replay the
 * original request, including multipart bodies that cannot be replayed safely.
 * Renewing before anything expires means an expiry 401 should not arise at all;
 * `handleUnauthorized` stays as the fallback for the case it is actually about,
 * which is a session that was revoked rather than one that aged out.
 */

import { accountManager } from './accountManager';

/**
 * How much of the token's life to use before renewing.
 *
 * Two thirds, so there is a full third of the window left for a retry, a slow
 * network or a laptop that was briefly asleep, before anything actually
 * expires.
 */
const RENEW_AT = 2 / 3;

/** Never sooner than this, so a pathological expiry cannot spin the loop. */
const MIN_DELAY_MS = 30_000;

/** Wake up at least this often to re-check, e.g. after the machine suspends. */
const MAX_DELAY_MS = 10 * 60_000;

/** Renew rather than wait if the token has less than this left. */
const IMMINENT_MS = 60_000;

let inFlight: Promise<boolean> | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;

/**
 * Exchange the stored refresh token for a new access token.
 *
 * Single-flight: several callers (a timer firing while a tab regains focus)
 * share one request rather than racing to overwrite each other's tokens.
 */
export const refreshActiveSession = async (): Promise<boolean> => {
  if (inFlight) return inFlight;

  const session = accountManager.getActiveSession();
  if (!session?.refreshToken) return false;

  inFlight = (async () => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: session.refreshToken }),
      });
      if (!response.ok) return false;

      const body = (await response.json()) as {
        success?: boolean;
        data?: { accessToken?: string; expiresIn?: number };
      };
      const accessToken = body.data?.accessToken;
      const expiresIn = body.data?.expiresIn;
      if (!accessToken || !expiresIn) return false;

      accountManager.updateTokens(accessToken, Date.now() + expiresIn * 1000);
      return true;
    } catch {
      // Offline, or the server is down. Not a reason to end the session —
      // the existing token is still valid for the rest of its window.
      return false;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
};

/** How long to wait before the next check, given the current session. */
const nextDelayMs = (now = Date.now()): number | null => {
  const session = accountManager.getActiveSession();
  if (!session?.expiresAt || !session.refreshToken) return null;

  const remaining = session.expiresAt - now;
  if (remaining <= IMMINENT_MS) return 0;

  // Renew once RENEW_AT of the ORIGINAL window has elapsed. The original
  // length is unknown here, so it is approximated from what is left: waiting
  // out (1 - RENEW_AT) of the remainder converges on the same point and needs
  // nothing stored.
  const wait = remaining * (1 - RENEW_AT);
  return Math.min(Math.max(wait, MIN_DELAY_MS), MAX_DELAY_MS);
};

const tick = async (): Promise<void> => {
  const delay = nextDelayMs();
  if (delay === null) return; // signed out — nothing to keep alive

  if (delay === 0) {
    await refreshActiveSession();
  }
  schedule();
};

const schedule = (): void => {
  if (timer) clearTimeout(timer);
  const delay = nextDelayMs();
  if (delay === null) return;
  timer = setTimeout(() => void tick(), Math.max(delay, MIN_DELAY_MS));
};

/**
 * Start keeping the active session fresh. Returns a stop function.
 *
 * Also renews when the tab regains focus: a timer does not fire while a laptop
 * is asleep, so a session can come back already past its renewal point.
 */
export const startSessionRefresh = (): (() => void) => {
  const onVisible = (): void => {
    if (document.visibilityState === 'visible') void tick();
  };

  void tick();
  document.addEventListener('visibilitychange', onVisible);

  return () => {
    if (timer) clearTimeout(timer);
    timer = null;
    document.removeEventListener('visibilitychange', onVisible);
  };
};

/** Test seam — module state outlives a test otherwise. */
export const _resetSessionRefreshForTests = (): void => {
  if (timer) clearTimeout(timer);
  timer = null;
  inFlight = null;
};
